import { Alert, Link, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import dayjs, { TIMEZONE } from "../../../utils/dayjs";
import { GetUserDetailsResponse, ParkingPermit } from "../types";
import DropdownSelectButton from "@components/DropdownMenu";
import { convertToMonthlyPriceStr, convertToPriceStr } from "@utils/money";
import useLocale from "@utils/useLocale";
import EndPermit from "@features/userDetails/components/EndPermit.tsx";
import { emailAuth } from "@utils/emailAuthUtils.ts";

interface ParkingPermitsProps {
  permits: ParkingPermit[];
  userDetails: GetUserDetailsResponse;
  onChange: (permitId: string, vehicle: string) => void;
}

interface PermitProps {
  permit: ParkingPermit;
  userLicencePlates: string[];
  onChange: (permitId: string, vehicle: string) => void;
}

interface PermitInfoProps {
  permit: ParkingPermit;
  matchingVehicle: string | undefined;
}

function PermitInfo({ permit, matchingVehicle }: PermitInfoProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const hasEnded = dayjs().isAfter(dayjs(permit.end_date));
  const aboutToEnd =
    !hasEnded &&
    permit.end_date &&
    dayjs(permit.end_date).diff(dayjs(), "days") <= 14;

  return (
    <>
      <Typography variant="h5" data-testid="parking-permit-type">
        {permit.title}
      </Typography>
      {hasEnded && (
        <Typography variant="body2" color="error">
          {t("purchaseParkingPermit.hasEnded")}
        </Typography>
      )}
      {aboutToEnd && (
        <Typography variant="body2" color="warning">
          {t("purchaseParkingPermit.aboutToEnd")}
        </Typography>
      )}
      <Typography variant="body1">
        {t("purchaseParkingPermit.validFrom")} <br />
        <b>{dayjs(permit.start_date).tz(TIMEZONE).format("LLLL")}</b>
      </Typography>
      {permit.end_date && (
        <Typography variant="body1">
          {t("purchaseParkingPermit.validTo")} <br />
          <b>{dayjs(permit.end_date).tz(TIMEZONE).format("LLLL")}</b>
        </Typography>
      )}
      {permit.price && !permit.end_date && (
        <Typography>
          {t("purchaseParkingPermit.recurrentBilling")}:<br />
          <b>
            {convertToMonthlyPriceStr(
              permit.price,
              locale,
              t("purchaseParkingPermit.dayAbrv"),
            )}
          </b>
        </Typography>
      )}
      {permit.paid_until && !permit.end_date && (
        <Typography>
          {t("purchaseParkingPermit.paidUntil")}:<br />
          <b>{dayjs(permit.paid_until).tz(TIMEZONE).format("LL")}</b>
        </Typography>
      )}
      {permit.permit_type === "SINGLE_PAYMENT" && (
        <Typography variant="body1">
          {t("purchaseParkingPermit.singlePaymentPrice")} <br />
          <b>{convertToPriceStr(permit.price, locale)}</b>
        </Typography>
      )}
      {!permit.permit_without_license_plates && (
        <Typography variant="body1" component="div">
          {t("userDetailsPage.parkingPermits.activeVehicle")}: <br />
          <b>
            {permit.vehicle_id || (
              <Alert severity="warning">
                {t("userDetailsPage.parkingPermits.noActiveVehicle")}
              </Alert>
            )}
          </b>
          {permit.vehicle_id && !matchingVehicle && (
            <Alert severity="warning">
              {t("userDetailsPage.parkingPermits.invalidActiveVehicle")}
            </Alert>
          )}
        </Typography>
      )}
    </>
  );
}

function Permit({ permit, userLicencePlates, onChange }: PermitProps) {
  const { t } = useTranslation();

  return (
    <Stack
      spacing={2}
      padding={2}
      border={1}
      borderColor="divider"
      borderRadius={4}
      data-testid={`parking-permit-${permit.id}`}
      sx={{ backgroundColor: "white" }}
    >
      <PermitInfo
        permit={permit}
        matchingVehicle={userLicencePlates.find(
          (vehicle) => vehicle === permit.vehicle_id,
        )}
      />
      <Stack direction="row" spacing={1}>
        {!permit.permit_without_license_plates && (
          <DropdownSelectButton
            label={t("userDetailsPage.parkingPermits.changeActiveVehicle")}
            options={userLicencePlates}
            selectedOption={permit.vehicle_id}
            onSelect={(vehicle) => {
              if (permit.vehicle_id !== vehicle) {
                onChange(permit.id, vehicle);
              }
            }}
          />
        )}
        {!permit.end_date && <EndPermit permit={permit} />}
      </Stack>
    </Stack>
  );
}

export default function ParkingPermits({
  permits,
  userDetails,
  onChange,
}: ParkingPermitsProps) {
  const { t } = useTranslation();
  const isEmailAuthenticated = !emailAuth.isTokenExpired();

  const vehicleOptions = userDetails.vehicles.map(
    (vehicle) => vehicle.licence_plate,
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h5">
        {t("userDetailsPage.parkingPermits.title")}
      </Typography>
      <Typography variant="subtitle1">
        {t("userDetailsPage.parkingPermits.description")}
      </Typography>
      {permits.length ? (
        permits
          .filter(
            (permit) =>
              !permit.end_date ||
              dayjs().subtract(30, "day").isBefore(dayjs(permit.end_date)),
          )
          .map((permit) => (
            <Permit
              key={permit.id}
              permit={permit}
              userLicencePlates={vehicleOptions}
              onChange={onChange}
            />
          ))
      ) : (
        <>
          <Typography variant="body1">
            <b>{t("userDetailsPage.parkingPermits.noParkingPermits")}</b>
          </Typography>
          <Link
            component={RouterLink}
            to={isEmailAuthenticated ? "/invitation/purchase" : "/purchase"}
          >
            {t("userDetailsPage.purchaseParkingPermit")}
          </Link>
        </>
      )}
    </Stack>
  );
}
